import React, { useEffect, useState, useRef } from 'react';
import API from '../api';
import { IcWatch, IcPlus, IcTrash, IcRefresh, IcMapPin, IcX, IcBattery, IcSignal, IcUsers } from './Icons';

const SCHOOL_ID = 1;

const SOURCE_LABEL = { gt06: 'Montre GT06', http: 'Traceur HTTP', app: 'Application' };
const SOURCE_COLOR = { gt06: 'var(--indigo)', http: 'var(--amber)', app: 'var(--emerald)' };
const TYPE_INFO = {
  gt06: { label: 'Montre GT06/GT02', desc: 'Wonlex, Sinotrack, Kid tracker chinois — connexion TCP port 5023', port: '5023 TCP' },
  http: { label: 'Traceur HTTP', desc: 'Sinotrack ST-901, Concox AT4 — envoie un GET vers ton URL', port: 'URL HTTP' },
  app:  { label: 'Application', desc: "Téléphone Android de l'enfant avec l'app Chef de Classe", port: 'Via API' },
};

function MapView({ positions }) {
  const mapRef = useRef(null);
  const leafletRef = useRef(null);
  const markersRef = useRef({});

  useEffect(() => {
    if (leafletRef.current || !mapRef.current) return;
    const L = window.L;
    if (!L) return;
    const map = L.map(mapRef.current).setView([5.36, -4.00], 12);
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { maxZoom: 19 }).addTo(map);
    leafletRef.current = map;
  }, []);

  useEffect(() => {
    const L = window.L;
    const map = leafletRef.current;
    if (!L || !map || !positions.length) return;

    positions.forEach(p => {
      const key = p.device_id;
      const minutesAgo = Math.floor((Date.now() - new Date(p.timestamp).getTime()) / 60000);
      const isRecent = minutesAgo < 15;
      const color = isRecent ? '#10B981' : '#94A3B8';

      const icon = L.divIcon({
        html: `<div style="background:${color};width:14px;height:14px;border-radius:50%;border:2.5px solid #fff;box-shadow:0 2px 8px rgba(0,0,0,0.25);"></div>`,
        className: '', iconSize: [14, 14], iconAnchor: [7, 7],
      });

      if (markersRef.current[key]) {
        markersRef.current[key].setLatLng([p.lat, p.lng]);
        markersRef.current[key].setIcon(icon);
        markersRef.current[key].getPopup()?.setContent(popupHtml(p, minutesAgo));
      } else {
        const m = L.marker([p.lat, p.lng], { icon })
          .bindPopup(popupHtml(p, minutesAgo), { maxWidth: 220 })
          .addTo(map);
        markersRef.current[key] = m;
      }
    });

    if (positions.length === 1) {
      map.setView([positions[0].lat, positions[0].lng], 15);
    } else {
      const bounds = L.latLngBounds(positions.map(p => [p.lat, p.lng]));
      map.fitBounds(bounds, { padding: [40, 40] });
    }
  }, [positions]);

  return <div ref={mapRef} style={{ height: '100%', borderRadius: 12 }} />;
}

function popupHtml(p, minutesAgo) {
  const timeStr = minutesAgo < 1 ? "À l'instant" : minutesAgo < 60 ? `Il y a ${minutesAgo} min` : `Il y a ${Math.floor(minutesAgo/60)}h`;
  return `<div style="font-family:sans-serif;font-size:13px;line-height:1.6">
    <b>${p.student_name || p.label}</b><br/>
    ${p.class_name ? `<span style="color:#64748B">${p.class_name}</span><br/>` : ''}
    ${p.speed > 0 ? `<span>Vitesse: ${p.speed} km/h</span><br/>` : ''}
    ${p.battery != null ? `<span>Batterie: ${p.battery}%</span><br/>` : ''}
    <span style="color:#94A3B8;font-size:11px">${timeStr} · ${SOURCE_LABEL[p.source] || p.source}</span>
  </div>`;
}

export default function GpsWatches() {
  const [devices, setDevices] = useState([]);
  const [positions, setPositions] = useState([]);
  const [students, setStudents] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ student_id: '', device_type: 'gt06', device_id: '', label: '' });
  const [msg, setMsg] = useState('');
  const [leafletLoaded, setLeafletLoaded] = useState(!!window.L);

  const loadDevices = () => API.get(`/gps/devices/school/${SCHOOL_ID}`).then(r => setDevices(r.data)).catch(() => {});
  const loadPositions = () => API.get(`/gps/positions/school/${SCHOOL_ID}/live`).then(r => setPositions(r.data)).catch(() => {});

  useEffect(() => {
    API.get(`/students/school/${SCHOOL_ID}`).then(r => setStudents(r.data)).catch(() => {});
    loadDevices();
    loadPositions();

    // Charger Leaflet si absent
    if (!window.L) {
      const link = document.createElement('link');
      link.rel = 'stylesheet'; link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
      document.head.appendChild(link);
      const script = document.createElement('script');
      script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
      script.onload = () => setLeafletLoaded(true);
      document.head.appendChild(script);
    }

    const timer = setInterval(loadPositions, 15000);
    return () => clearInterval(timer);
  }, []);

  const addDevice = async () => {
    setMsg('');
    if (!form.student_id) return setMsg('Sélectionnez un élève');
    const s = students.find(s => s.id === +form.student_id);
    try {
      await API.post('/gps/devices', {
        student_id: +form.student_id,
        school_id: SCHOOL_ID,
        device_type: form.device_type,
        device_id: form.device_id || undefined,
        label: form.label || (s ? `${s.first_name} ${s.last_name}` : 'Appareil'),
      });
      setForm({ student_id: '', device_type: 'gt06', device_id: '', label: '' });
      setShowForm(false);
      loadDevices();
    } catch (e) { setMsg(e.response?.data?.detail || 'Erreur'); }
  };

  const removeDevice = async (id) => {
    if (!window.confirm('Désactiver cet appareil ?')) return;
    await API.delete(`/gps/devices/${id}`);
    loadDevices();
  };

  const activeCount = positions.filter(p => {
    const mins = (Date.now() - new Date(p.timestamp).getTime()) / 60000;
    return mins < 15;
  }).length;

  return (
    <div style={{ padding: 28 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <div>
          <h1 className="page-title">Montres GPS</h1>
          <p className="page-subtitle">Suivi temps réel des élèves • {activeCount} actif{activeCount > 1 ? 's' : ''} en ce moment</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-ghost" onClick={() => { loadDevices(); loadPositions(); }}>
            <IcRefresh size={14} strokeWidth={2} /> Actualiser
          </button>
          <button className="btn btn-primary" onClick={() => setShowForm(!showForm)}>
            <IcPlus size={15} strokeWidth={2.5} /> Ajouter
          </button>
        </div>
      </div>

      {/* Carte principale */}
      <div className="card" style={{ height: 420, padding: 0, overflow: 'hidden', marginBottom: 20 }}>
        {!leafletLoaded ? (
          <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 12, color: 'var(--slate-400)' }}>
            <IcMapPin size={32} stroke="var(--slate-300)" />
            <div style={{ fontSize: 13 }}>Chargement de la carte...</div>
          </div>
        ) : positions.length === 0 ? (
          <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 12, color: 'var(--slate-400)' }}>
            <IcWatch size={36} stroke="var(--slate-300)" strokeWidth={1.5} />
            <div style={{ fontSize: 13 }}>Aucune position reçue</div>
            <div style={{ fontSize: 12, color: 'var(--slate-300)' }}>Les positions apparaîtront ici dès qu'une montre envoie ses données</div>
          </div>
        ) : (
          <MapView positions={positions} />
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 380px', gap: 20, alignItems: 'start' }}>

        {/* Liste des appareils */}
        <div>
          {/* Formulaire */}
          {showForm && (
            <div className="card" style={{ padding: 22, marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
                <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--slate-800)' }}>Nouvel appareil GPS</div>
                <button className="btn btn-ghost btn-icon" onClick={() => setShowForm(false)}><IcX size={14} /></button>
              </div>

              {/* Choix du type */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 16 }}>
                {Object.entries(TYPE_INFO).map(([type, info]) => (
                  <div key={type} onClick={() => setForm(f => ({ ...f, device_type: type }))} style={{
                    padding: '12px', borderRadius: 10, cursor: 'pointer',
                    border: `2px solid ${form.device_type === type ? 'var(--indigo)' : 'var(--slate-200)'}`,
                    background: form.device_type === type ? 'var(--indigo-l)' : 'var(--slate-50)',
                    transition: 'all 0.15s',
                  }}>
                    <div style={{ fontWeight: 600, fontSize: 12, color: form.device_type === type ? 'var(--indigo)' : 'var(--slate-700)', marginBottom: 3 }}>{info.label}</div>
                    <div style={{ fontSize: 11, color: 'var(--slate-400)', lineHeight: 1.4 }}>{info.desc}</div>
                  </div>
                ))}
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
                <div>
                  <label className="label">Élève</label>
                  <select className="input" value={form.student_id} onChange={e => setForm(f => ({ ...f, student_id: e.target.value }))}>
                    <option value="">Sélectionner...</option>
                    {students.map(s => <option key={s.id} value={s.id}>{s.first_name} {s.last_name} — {s.class_name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="label">Libellé</label>
                  <input className="input" placeholder="Montre de Kofi" value={form.label} onChange={e => setForm(f => ({ ...f, label: e.target.value }))} />
                </div>
                {form.device_type !== 'app' && (
                  <div style={{ gridColumn: '1/-1' }}>
                    <label className="label">
                      {form.device_type === 'gt06' ? 'IMEI de la montre (15 chiffres)' : 'ID de l\'appareil (IMEI ou code)'}
                    </label>
                    <input className="input" placeholder={form.device_type === 'gt06' ? '867123456789012' : 'laisser vide = auto-généré'} value={form.device_id} onChange={e => setForm(f => ({ ...f, device_id: e.target.value }))} style={{ fontFamily: 'monospace' }} />
                  </div>
                )}
              </div>

              {msg && <div style={{ padding: '8px 12px', background: 'var(--red-l)', color: 'var(--red)', borderRadius: 7, fontSize: 12, marginBottom: 12 }}>{msg}</div>}

              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-primary" onClick={addDevice}><IcPlus size={14} strokeWidth={2.5} /> Enregistrer</button>
                <button className="btn btn-ghost" onClick={() => setShowForm(false)}>Annuler</button>
              </div>

              {/* Instructions de configuration */}
              {form.device_type === 'gt06' && (
                <div style={{ marginTop: 16, padding: 14, background: 'rgba(99,102,241,0.06)', borderRadius: 10, border: '1px solid rgba(99,102,241,0.15)', fontSize: 11, color: 'var(--slate-600)', lineHeight: 1.8 }}>
                  <div style={{ fontWeight: 700, color: 'var(--indigo)', marginBottom: 6 }}>Configuration de la montre GT06</div>
                  <div>Envoyez ces SMS à la montre depuis le numéro admin :</div>
                  <div style={{ fontFamily: 'monospace', background: 'rgba(0,0,0,0.05)', padding: '8px 10px', borderRadius: 6, marginTop: 6 }}>
                    {"SERVER,1,chefdeclasse.com,5023,0#"}<br/>
                    {"UPLOAD,30#"}
                  </div>
                </div>
              )}
              {form.device_type === 'http' && (
                <div style={{ marginTop: 16, padding: 14, background: 'rgba(245,158,11,0.06)', borderRadius: 10, border: '1px solid rgba(245,158,11,0.2)', fontSize: 11, color: 'var(--slate-600)', lineHeight: 1.8 }}>
                  <div style={{ fontWeight: 700, color: 'var(--amber)', marginBottom: 6 }}>URL de configuration (Sinotrack / Concox)</div>
                  <div style={{ fontFamily: 'monospace', background: 'rgba(0,0,0,0.05)', padding: '8px 10px', borderRadius: 6, wordBreak: 'break-all' }}>
                    https://chefdeclasse.com/api/gps/tracker?id=IMEI&lat=%LAT%&lng=%LNG%&speed=%SPD%&bat=%BAT%
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Liste devices */}
          <div className="card" style={{ overflow: 'hidden' }}>
            {devices.length === 0 ? (
              <div className="empty" style={{ padding: '40px 0' }}>
                <IcWatch size={36} stroke="var(--slate-300)" strokeWidth={1.5} />
                <p style={{ marginTop: 12 }}>Aucun appareil enregistré</p>
              </div>
            ) : (
              <table>
                <thead>
                  <tr>
                    <th>Élève / Appareil</th>
                    <th>Type</th>
                    <th>ID</th>
                    <th>Dernière position</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {devices.map(dev => {
                    const pos = dev.last_position;
                    const mins = pos ? Math.floor((Date.now() - new Date(pos.timestamp).getTime()) / 60000) : null;
                    const fresh = mins !== null && mins < 15;
                    return (
                      <tr key={dev.id}>
                        <td>
                          <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--slate-800)' }}>{dev.label}</div>
                          {dev.student_name && <div style={{ fontSize: 11, color: 'var(--slate-400)' }}>{dev.student_name}</div>}
                        </td>
                        <td>
                          <span style={{ padding: '2px 8px', borderRadius: 999, fontSize: 11, fontWeight: 600, background: `${SOURCE_COLOR[dev.device_type]}15`, color: SOURCE_COLOR[dev.device_type] }}>
                            {TYPE_INFO[dev.device_type]?.label || dev.device_type}
                          </span>
                        </td>
                        <td style={{ fontFamily: 'monospace', fontSize: 11, color: 'var(--slate-500)' }}>
                          {dev.device_id.length > 16 ? dev.device_id.slice(0, 15) + '…' : dev.device_id}
                        </td>
                        <td>
                          {pos ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              <div style={{ width: 8, height: 8, borderRadius: '50%', background: fresh ? 'var(--emerald)' : 'var(--slate-300)', flexShrink: 0 }} />
                              <div>
                                <div style={{ fontSize: 12, color: 'var(--slate-700)' }}>{pos.lat.toFixed(5)}, {pos.lng.toFixed(5)}</div>
                                <div style={{ fontSize: 11, color: 'var(--slate-400)' }}>
                                  {mins === 0 ? "À l'instant" : mins < 60 ? `Il y a ${mins} min` : `Il y a ${Math.floor(mins/60)}h`}
                                  {pos.battery != null && ` · ${pos.battery}%`}
                                  {pos.speed > 0 && ` · ${pos.speed} km/h`}
                                </div>
                              </div>
                            </div>
                          ) : (
                            <span style={{ fontSize: 12, color: 'var(--slate-300)' }}>En attente...</span>
                          )}
                        </td>
                        <td>
                          <button className="btn btn-danger btn-sm btn-icon" onClick={() => removeDevice(dev.id)}>
                            <IcTrash size={13} strokeWidth={2} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* Panel info */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

          {/* Stats positions live */}
          <div className="card" style={{ padding: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
              <IcSignal size={15} stroke="var(--emerald)" strokeWidth={2} />
              <span style={{ fontWeight: 600, fontSize: 14, color: 'var(--slate-800)' }}>Positions en direct</span>
              <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--slate-400)' }}>Actualise toutes les 15s</span>
            </div>
            {positions.length === 0 ? (
              <div style={{ textAlign: 'center', color: 'var(--slate-300)', fontSize: 12, padding: '12px 0' }}>Aucune donnée reçue</div>
            ) : (
              positions.map(p => {
                const mins = Math.floor((Date.now() - new Date(p.timestamp).getTime()) / 60000);
                const fresh = mins < 15;
                return (
                  <div key={p.device_id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 0', borderBottom: '1px solid var(--slate-100)' }}>
                    <div style={{ width: 32, height: 32, borderRadius: 8, background: fresh ? 'var(--emerald-l)' : 'var(--slate-100)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <IcWatch size={14} stroke={fresh ? 'var(--emerald)' : 'var(--slate-400)'} strokeWidth={2} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: 12, color: 'var(--slate-800)' }}>{p.student_name || p.label}</div>
                      <div style={{ fontSize: 11, color: 'var(--slate-400)' }}>
                        {mins === 0 ? "À l'instant" : mins < 60 ? `${mins} min` : `${Math.floor(mins/60)}h`}
                        {p.battery != null && ` · ${p.battery}%`}
                      </div>
                    </div>
                    <div style={{ width: 8, height: 8, borderRadius: '50%', background: fresh ? 'var(--emerald)' : 'var(--slate-300)', flexShrink: 0 }} />
                  </div>
                );
              })
            )}
          </div>

          {/* Guide d'achat */}
          <div className="card" style={{ padding: 20 }}>
            <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--slate-800)', marginBottom: 14 }}>Montres compatibles</div>
            {[
              { name: 'Wonlex GW700S', type: 'GT06', price: '~20 000 FCFA', badge: 'Recommandé', badgeColor: 'var(--emerald)' },
              { name: 'Sinotrack ST-901', type: 'HTTP', price: '~15 000 FCFA', badge: 'Simple', badgeColor: 'var(--amber)' },
              { name: 'Xplora X5 Play', type: 'GT06', price: '~60 000 FCFA', badge: 'Premium', badgeColor: 'var(--indigo)' },
              { name: 'Concox AT4', type: 'HTTP', price: '~25 000 FCFA', badge: '', badgeColor: '' },
            ].map(w => (
              <div key={w.name} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--slate-100)' }}>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--slate-800)' }}>{w.name}</div>
                  <div style={{ fontSize: 11, color: 'var(--slate-400)' }}>{w.type} · {w.price}</div>
                </div>
                {w.badge && <span style={{ padding: '2px 8px', borderRadius: 999, fontSize: 10, fontWeight: 600, background: `${w.badgeColor}18`, color: w.badgeColor }}>{w.badge}</span>}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
